import { CustomizationService } from './../../../services/customization.service';
import { ToastrService } from 'ngx-toastr';
import { ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, finalize } from 'rxjs';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MenuService } from '../../../services/menu.service';
import { FNFResponse } from '../../../models/response';
import { NgxSpinnerService } from 'ngx-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AddCharacteristicsModalComponent } from './modal/add-characteristics-modal/add-characteristics-modal.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationDialogComponent } from '../../controls/fnf-confirmation/confirmation-dialog/confirmation-dialog.component';
import { AddServingTimeModalComponent } from './modal/add-serving-time-modal/add-serving-time-modal.component';
import { StationService } from '../../../services/station.service';
import { AddStationModalComponent } from '../station/modal/add-station-modal/add-station-modal.component';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { AddCustomizationModalComponent } from '../customization/modal/add-customization-modal/add-customization-modal.component';
import { ImageCropingComponent } from '../../modals/image-croping/image-croping.component';
import {
  croppedImageFile,
  sourceImageError,
  uploadSizeError,
} from '../../../utils/image-export.util';
import { RestaurantService } from '../../../services/restaurant.service';
import { FnfAiGenerateButtonComponent } from '../../controls/fnf-ai-generate-button/fnf-ai-generate-button.component';
import { groupsOfType } from '../../../utils/serving-hours.util';
import { isFnfLike, isPosManaged } from '../../../utils/pos-provider.util';

@Component({
  selector: 'app-add-menu',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIcon,
    MatTooltipModule,
    MatDialogModule,
    MatCheckboxModule,
    NgMultiSelectDropDownModule,
    FnfAiGenerateButtonComponent,
  ],
  templateUrl: './add-menu.component.html',
  styleUrl: './add-menu.component.css',
})
export class AddMenuComponent {
  menuService = inject(MenuService);
  ngxSpinnerService = inject(NgxSpinnerService);
  stationService = inject(StationService);
  customizationService = inject(CustomizationService);
  restaurantService = inject(RestaurantService);
  dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  // AI generation runs per field with its own busy flag, never the global
  // spinner, so the rest of the form stays usable while a call is in flight.
  generating = {
    description: false,
    ingredients: false,
    tags: false,
    prepTime: false,
    image: false,
  };

  inputField: any;
  tagInput: any;
  // ingredients: string[] = [' Rice', 'Chicken', 'Egg', 'Onion'];
  menuIngredients: string[] = [];
  newIngredient: string = '';

  // tagsList: string[] = ['Spicy', 'Salt', 'Hot'];
  tagsList: string[] = [];
  newTag: string = '';

  servingTimes: any = [];

  imagePreview: string | null = null;
  uploadedFile: File | null = null;
  images: { file: File | null; url: string }[] = [];

  menuForm: FormGroup;
  productForm: FormGroup;

  closeResult = '';
  coverImageSizeError = '';
  additionalImageSizeError = '';
  noValidate: boolean = false;
  isSubmit: boolean = false;
  selectedStatus: string = 'draft';

  exitingMenuId: any;

  exitingTypes: any = [];
  exitingSKUs: any = [];
  exitingSKUMsg: any = '';
  exitingMenu: any;
  private exitingMenuServingTime: any[] | undefined;

  selectedStation: any;
  stations: any = [];

  customizations: any = [];
  existingCustomization: any;
  isCustomizationLoaded: boolean = false;

  dropdownList: any = [];
  selectedItems: any = [];
  dropdownSettings: any = {
    singleSelection: false,
    idField: 'item_id',
    textField: 'item_text',
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    itemsShowLimit: 2,
    allowSearchFilter: true,
    defaultOpen: false,
  };
  displayLoading: boolean = false;
  imageErrors: Set<string | number> = new Set();
  isAnyCategorySelected: boolean = false;
  // private characteristicsModal: Modal | null = null;
  restaurantPosProvider: string = 'fnf';
  isNotFnfHardware: boolean = false;
  restaurantServingHours: any;
  formattedRestaurantServingHours: any;

  readonly servingTimeDays: { key: string; label: string }[] = [
    { key: 'Sun', label: 'Sunday' },
    { key: 'Mon', label: 'Monday' },
    { key: 'Tue', label: 'Tuesday' },
    { key: 'Wed', label: 'Wednesday' },
    { key: 'Thu', label: 'Thursday' },
    { key: 'Fri', label: 'Friday' },
    { key: 'Sat', label: 'Saturday' },
  ];
  servingTimeForm!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
  ) {
    this.initServingTimeForm();

    this.restaurantService.currentRestaurantDetails$.subscribe((restaurant: any) => {
      if (restaurant) {
        // Menu times start from the opening hours, not reservation hours.
        this.restaurantServingHours = groupsOfType(
          restaurant.serving_hours?.serving_hours,
          'store_hours',
        );
        this.formattedRestaurantServingHours = this.mapRestaurantServingHoursToMenuServingTime(
          this.restaurantServingHours,
        );
        this.refreshServingTimeFromLoadedData();
      }
    });

    // Initialize the form
    this.menuForm = this.fb.group({
      image: [null, Validators.required], // Form control for the image file
    });

    this.productForm = this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.maxLength(120),
          Validators.minLength(2),
          this.notNumbersOnlyValidator(),
        ],
      ],
      description: ['', [Validators.required, Validators.maxLength(200)]],
      coverImage: [null],
      image_url: [''],
      additional_images: this.fb.array([], [Validators.maxLength(4)]),
      originalPrice: [0, [Validators.required, Validators.min(0)]],
      offerPrice: [0, [Validators.min(0)]],
      sku: ['', [this.skuExistingValidator()]],
      stockStatus: [true, Validators.required],
      classification: this.fb.array([]),
      customization: this.fb.array([]),
      characteristics: this.fb.array([]),
      ingredients: this.fb.array([]),
      ingredientName: [''],
      preparationTime: [5, [Validators.required, Validators.min(5)]],
      servingSize: [1, [Validators.required, Validators.min(1)]],
      tags: this.fb.array([]),
      tagName: [''],
      additionalNotes: [''],
      status: ['draft'],
      orderTypes: this.fb.group(
        { is_dine_in: [true], is_togo: [true], is_catering: [false] },
        { validators: this.atLeastOneOrderTypeValidator() },
      ),
      // servingTime: [0, [Validators.required]]
    });

    // Reactive binding for ng-multiselect-dropdown selections
    this.productForm.addControl('selectedCustomizations', this.fb.control([]));

    this.getTypesByRestaurant();
    this.getExitingSKU();
    // this.getServingTimes();
    this.getExitingCustomizations();
    this.getStations();
  }

  private mapRestaurantServingHoursToMenuServingTime(restaurantServingHours: any[]): any[] {
    const hoursByDay = new Map<string, any[]>();
    for (const entry of restaurantServingHours || []) {
      const hours = (entry.hours || []).map((hour: any) => ({
        start_time: hour.start_time,
        end_time: hour.end_time,
      }));
      for (const day of entry.days_of_the_week || []) {
        hoursByDay.set(day, [...(hoursByDay.get(day) || []), ...hours]);
      }
    }
    return Array.from(hoursByDay, ([day, hours]) => ({ day, hours }));
  }

  private initServingTimeForm(): void {
    const group = this.fb.group({});
    this.servingTimeDays.forEach((d) => {
      group.addControl(
        d.key,
        this.fb.group({
          enabled: [false],
          hours: this.fb.array([this.createServingTimeHour()]),
        }),
      );
    });
    this.servingTimeForm = group;
  }

  private createServingTimeHour(
    start_time: string = '',
    end_time: string = '',
    next_day: boolean = false,
  ): FormGroup {
    return this.fb.group(
      {
        start_time: [start_time, Validators.required],
        end_time: [end_time, Validators.required],
        // Starts after midnight, inside this day's next-day restaurant hours. Sent as the
        // next day's slot (FNF-1237).
        next_day: [next_day],
      },
      { validators: this.servingTimeRangeValidator.bind(this) },
    );
  }

  private servingTimeRangeValidator(control: AbstractControl): ValidationErrors | null {
    const start = control.get('start_time')?.value;
    const end = control.get('end_time')?.value;
    if (!start || !end) {
      return null;
    }
    // An end earlier than the start closes the next day (FNF-1237); only an equal end is invalid.
    return end === start ? { timeRangeInvalid: true } : null;
  }

  private timeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  private readonly minutesPerDay = 24 * 60;

  // Marks a start option after midnight, in the day's next-day restaurant hours.
  private readonly nextDayStartSuffix = '+1';

  /** Minutes from start to end; an end earlier than the start runs past midnight. */
  private servingSpanMinutes(start: string, end: string): number {
    return (
      (this.timeToMinutes(end) - this.timeToMinutes(start) + this.minutesPerDay) %
      this.minutesPerDay
    );
  }

  /** The end in minutes from the start's midnight: an end earlier than the start is next day. */
  private servingEndMinutes(start: string, end: string): number {
    return this.timeToMinutes(start) + this.servingSpanMinutes(start, end);
  }

  /** A slot's start in minutes from its day's midnight; a next-day start is 24:00 or later. */
  private servingStartMinutes(hour: AbstractControl | null | undefined): number | null {
    const start = hour?.get('start_time')?.value;
    if (!start) {
      return null;
    }
    return this.timeToMinutes(start) + (hour?.get('next_day')?.value ? this.minutesPerDay : 0);
  }

  /** A slot's start and end in minutes from its day's midnight, once both are set. */
  private servingSlotMinutes(
    hour: AbstractControl | null | undefined,
  ): { start: number; end: number } | null {
    const start = this.servingStartMinutes(hour);
    const startTime = hour?.get('start_time')?.value;
    const endTime = hour?.get('end_time')?.value;
    if (start === null || !endTime) {
      return null;
    }
    return { start, end: start + this.servingSpanMinutes(startTime, endTime) };
  }

  isServingTimeNextDay(day: string, hourIndex: number, type: 'start' | 'end'): boolean {
    const hour = this.getServingTimeHours(day).at(hourIndex);
    if (type === 'start') {
      return !!hour?.get('start_time')?.value && !!hour?.get('next_day')?.value;
    }
    const slot = this.servingSlotMinutes(hour);
    return !!slot && slot.end >= this.minutesPerDay;
  }

  private nextServingTimeDay(day: string): string {
    const index = this.servingTimeDays.findIndex((d) => d.key === day);
    return this.servingTimeDays[(index + 1) % this.servingTimeDays.length].key;
  }

  private previousServingTimeDay(day: string): { key: string; label: string } {
    const index = this.servingTimeDays.findIndex((d) => d.key === day);
    const count = this.servingTimeDays.length;
    return this.servingTimeDays[(index + count - 1) % count];
  }

  getServingTimeDayGroup(day: string): FormGroup {
    return this.servingTimeForm.get(day) as FormGroup;
  }

  getServingTimeHours(day: string): FormArray {
    return this.getServingTimeDayGroup(day).get('hours') as FormArray;
  }

  isServingTimeDayEnabled(day: string): boolean {
    return !!this.getServingTimeDayGroup(day)?.get('enabled')?.value;
  }

  toggleServingTimeDay(day: string): void {
    if (!this.isServingTimeDayAllowed(day)) {
      return;
    }
    const enabledControl = this.getServingTimeDayGroup(day).get('enabled');
    enabledControl?.setValue(!enabledControl.value);
    // Next-day slots on this day or the previous one may now overlap, or stop overlapping.
    this.validateOverlappingServingTimeHours(day);
    this.validateOverlappingServingTimeHours(this.nextServingTimeDay(day));
  }

  isServingTimeDayAllowed(day: string): boolean {
    // No restaurant hours configured at all: don't restrict menu days.
    if (!this.restaurantServingHours || this.restaurantServingHours.length === 0) {
      return true;
    }
    return (this.formattedRestaurantServingHours || []).some((entry: any) => entry.day === day);
  }

  getRestaurantHoursForDay(day: string): { start_time: string; end_time: string }[] {
    const entry = (this.formattedRestaurantServingHours || []).find((e: any) => e.day === day);
    return entry ? entry.hours : [];
  }

  private readonly servingTimeStepMinutes = 15;

  readonly timeOptionsDisplay: { value: string; display: string }[] =
    this.buildTimeOptionsDisplay();

  private buildTimeOptionsDisplay(): { value: string; display: string }[] {
    const options: { value: string; display: string }[] = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += this.servingTimeStepMinutes) {
        const time24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        options.push({ value: time24, display: this.convertTo12HourFormat(time24) });
      }
    }
    return options;
  }

  // Convert 24-hour "HH:MM" (stored/validated value) to 12-hour "hh:mm AM/PM" (display only).
  convertTo12HourFormat(time24: string): string {
    if (!time24) {
      return '';
    }
    const [hoursStr, minutes] = time24.split(':');
    const hour24 = parseInt(hoursStr, 10);
    let hour12 = hour24;
    const period = hour24 >= 12 ? 'PM' : 'AM';
    if (hour12 === 0) {
      hour12 = 12;
    } else if (hour12 > 12) {
      hour12 -= 12;
    }
    return `${hour12.toString().padStart(2, '0')}:${minutes} ${period}`;
  }

  getServingTimeDisplay(day: string, hourIndex: number, type: 'start' | 'end'): string {
    const hourGroup = this.getServingTimeHours(day).at(hourIndex);
    const value = hourGroup?.get(type === 'start' ? 'start_time' : 'end_time')?.value;
    return value ? this.convertTo12HourFormat(value) : 'Select time';
  }

  /** The restaurant's open hours for this day, in minutes from the day's midnight. */
  private getRestaurantRangesMinutes(
    day: string,
  ): { start: number; end: number; end_time: string }[] {
    return this.getRestaurantHoursForDay(day).map((range) => ({
      start: this.timeToMinutes(range.start_time),
      end: this.servingEndMinutes(range.start_time, range.end_time),
      end_time: range.end_time,
    }));
  }

  /** The restaurant's open-hours range (if any) for this day that contains the given minute. */
  private getRestaurantRangeContaining(
    day: string,
    minutes: number,
  ): { start: number; end: number; end_time: string } | undefined {
    return this.getRestaurantRangesMinutes(day).find(
      (range) => minutes >= range.start && minutes <= range.end,
    );
  }

  /** No restaurant hours configured at all for this day: don't constrain menu times either. */
  private hasRestaurantHoursConstraint(day: string): boolean {
    return (
      !!this.restaurantServingHours &&
      this.restaurantServingHours.length > 0 &&
      this.getRestaurantHoursForDay(day).length > 0
    );
  }

  /**
   * Start options for this day. Restaurant hours that close the next day also offer their
   * hours after midnight, marked "(next day)" (Mon 8 PM – 4 AM offers Mon 1:00 AM next day).
   */
  private getServingStartTimeOptions(
    day: string,
  ): { value: string; display: string; minutes: number }[] {
    const sameDay = this.timeOptionsDisplay.map((o) => ({
      ...o,
      minutes: this.timeToMinutes(o.value),
    }));
    if (!this.hasRestaurantHoursConstraint(day)) {
      return sameDay;
    }
    const nextDay = this.timeOptionsDisplay.map((o) => ({
      value: `${o.value}${this.nextDayStartSuffix}`,
      display: `${o.display} (next day)`,
      minutes: this.timeToMinutes(o.value) + this.minutesPerDay,
    }));
    return [...sameDay, ...nextDay].filter((o) => {
      const range = this.getRestaurantRangeContaining(day, o.minutes);
      return !!range && o.minutes < range.end;
    });
  }

  getServingEndTimeOptions(
    day: string,
    hourIndex: number,
  ): { value: string; display: string; minutes: number }[] {
    const hour = this.getServingTimeHours(day).at(hourIndex);
    const startTime = hour?.get('start_time')?.value;
    const startMinutes = this.servingStartMinutes(hour);
    if (!startTime || startMinutes === null) {
      return [];
    }
    const minEndMinutes = startMinutes + this.servingTimeStepMinutes;
    // Times earlier than the start close the next day, so they list after the later ones.
    const toOption = (o: { value: string; display: string }) => ({
      ...o,
      minutes: startMinutes + this.servingSpanMinutes(startTime, o.value),
    });
    let options = this.timeOptionsDisplay.map(toOption).filter((o) => o.minutes >= minEndMinutes);

    if (this.hasRestaurantHoursConstraint(day)) {
      const range = this.getRestaurantRangeContaining(day, startMinutes);
      const rangeEndMinutes = range ? range.end : startMinutes;
      options = options.filter((o) => o.minutes <= rangeEndMinutes);
      // Offer the restaurant's exact close time even if it falls inside the step gap.
      if (
        range &&
        rangeEndMinutes >= minEndMinutes &&
        !options.some((o) => o.value === range.end_time)
      ) {
        options.push(
          toOption({ value: range.end_time, display: this.convertTo12HourFormat(range.end_time) }),
        );
      }
    } else if (startMinutes < this.timeToMinutes('23:59')) {
      // Unconstrained (no restaurant hours set): always offer "until midnight".
      options.push(toOption({ value: '23:59', display: '11:59 PM' }));
    }
    return options
      .sort((a, b) => a.minutes - b.minutes)
      .map((o) =>
        o.minutes >= this.minutesPerDay ? { ...o, display: `${o.display} (next day)` } : o,
      );
  }

  /** Other slots (same day) that already have both a start and end time set. */
  private getOtherServingTimeSlots(
    day: string,
    excludeHourIndex: number,
  ): { start_time: string; end_time: string; start: number; end: number }[] {
    if (!this.isServingTimeDayEnabled(day)) {
      return [];
    }
    const slots: { start_time: string; end_time: string; start: number; end: number }[] = [];
    this.getServingTimeHours(day).controls.forEach((c, idx) => {
      const minutes = this.servingSlotMinutes(c);
      if (idx !== excludeHourIndex && minutes) {
        slots.push({
          start_time: c.get('start_time')?.value,
          end_time: c.get('end_time')?.value,
          ...minutes,
        });
      }
    });
    return slots;
  }

  /**
   * Turns a flat option list into dropdown rows, collapsing any run of options that falls
   * inside another slot's already-set range into a single disabled "start – end" row —
   * so the user sees why those times are unavailable instead of them just vanishing.
   */
  private mergeBlockedRanges(
    candidates: { value: string; display: string; minutes: number }[],
    day: string,
    hourIndex: number,
  ): { disabled: boolean; value?: string; display: string }[] {
    const otherSlots = this.getOtherServingTimeSlots(day, hourIndex);
    const items: { disabled: boolean; value?: string; display: string }[] = [];
    let i = 0;
    while (i < candidates.length) {
      const opt = candidates[i];
      const blocking = otherSlots.find((r) => opt.minutes >= r.start && opt.minutes <= r.end);
      if (blocking) {
        let j = i;
        while (j < candidates.length && candidates[j].minutes <= blocking.end) {
          j++;
        }
        items.push({
          disabled: true,
          display: `${this.convertTo12HourFormat(blocking.start_time)} – ${this.convertTo12HourFormat(blocking.end_time)}`,
        });
        i = j;
      } else {
        items.push({ disabled: false, value: opt.value, display: opt.display });
        i++;
      }
    }
    return items;
  }

  getServingStartTimeItems(
    day: string,
    hourIndex: number,
  ): { disabled: boolean; value?: string; display: string }[] {
    return this.mergeBlockedRanges(this.getServingStartTimeOptions(day), day, hourIndex);
  }

  getServingEndTimeItems(
    day: string,
    hourIndex: number,
  ): { disabled: boolean; value?: string; display: string }[] {
    return this.mergeBlockedRanges(this.getServingEndTimeOptions(day, hourIndex), day, hourIndex);
  }

  canSelectServingEndTime(day: string, hourIndex: number): boolean {
    return !!this.getServingTimeHours(day).at(hourIndex)?.get('start_time')?.value;
  }

  selectServingTime(day: string, hourIndex: number, type: 'start' | 'end', time: string): void {
    const hourGroup = this.getServingTimeHours(day).at(hourIndex) as FormGroup;
    if (type === 'start') {
      const nextDay = time.endsWith(this.nextDayStartSuffix);
      hourGroup.get('next_day')?.setValue(nextDay);
      if (nextDay) {
        time = time.slice(0, -this.nextDayStartSuffix.length);
      }
    }
    const control = hourGroup.get(type === 'start' ? 'start_time' : 'end_time');
    control?.setValue(time);
    control?.markAsTouched();
    control?.markAsDirty();

    if (type === 'start') {
      const endControl = hourGroup.get('end_time');
      if (endControl?.value) {
        const stillValid = this.getServingEndTimeOptions(day, hourIndex).some(
          (o) => o.value === endControl.value,
        );
        if (!stillValid) {
          endControl.setValue('');
        }
      }
    }
    hourGroup.updateValueAndValidity();
    this.validateDuplicateServingTimeHours(day);
    this.validateOverlappingServingTimeHours(day);
    this.validateServingTimeWithinRestaurantHours(day);
    this.validateOverlappingServingTimeHours(this.nextServingTimeDay(day));
  }

  addServingTimeHour(day: string): void {
    this.getServingTimeHours(day).push(this.createServingTimeHour());
  }

  /** False once every remaining time (within restaurant hours) is already covered by an existing slot. */
  canAddServingTimeHour(day: string): boolean {
    const hours = this.getServingTimeHours(day);
    return this.getServingStartTimeItems(day, hours.length).some((item) => !item.disabled);
  }

  removeServingTimeHour(day: string, index: number): void {
    const hours = this.getServingTimeHours(day);
    if (hours.length > 1) {
      hours.removeAt(index);
      this.validateDuplicateServingTimeHours(day);
      this.validateOverlappingServingTimeHours(day);
      this.validateServingTimeWithinRestaurantHours(day);
      this.validateOverlappingServingTimeHours(this.nextServingTimeDay(day));
    }
  }

  /** Flags a slot with a `outsideRestaurantHours` error when it isn't fully inside a restaurant open-hours range for this day. */
  private validateServingTimeWithinRestaurantHours(day: string): void {
    const hours = this.getServingTimeHours(day);

    hours.controls.forEach((control) => {
      const errors = control.errors;
      if (errors && errors['outsideRestaurantHours']) {
        const newErrors: any = { ...errors };
        delete newErrors['outsideRestaurantHours'];
        control.setErrors(Object.keys(newErrors).length ? newErrors : null);
      }
    });

    if (!this.hasRestaurantHoursConstraint(day)) {
      return;
    }

    hours.controls.forEach((control) => {
      const slot = this.servingSlotMinutes(control);
      if (!slot) {
        return;
      }
      const range = this.getRestaurantRangeContaining(day, slot.start);
      const withinRange = !!range && slot.end <= range.end;
      if (!withinRange) {
        control.setErrors({ ...(control.errors || {}), outsideRestaurantHours: true });
        control.markAsTouched();
        control.markAsDirty();
      }
    });
  }

  hasServingTimeOutsideRestaurantHoursError(day: string, hourIndex: number): boolean {
    return !!this.getServingTimeHours(day).at(hourIndex)?.hasError('outsideRestaurantHours');
  }

  hasServingTimeOutsideRestaurantHours(day: string): boolean {
    return this.getServingTimeHours(day).controls.some((c) => c.hasError('outsideRestaurantHours'));
  }

  hasServingTimeIncompleteError(day: string, hourIndex: number): boolean {
    if (!this.isServingTimeDayEnabled(day)) {
      return false;
    }
    const hour = this.getServingTimeHours(day).at(hourIndex);
    return !hour?.get('start_time')?.value || !hour?.get('end_time')?.value;
  }

  hasServingTimeIncompleteHours(day: string): boolean {
    if (!this.isServingTimeDayEnabled(day)) {
      return false;
    }
    return this.getServingTimeHours(day).controls.some(
      (c) => !c.get('start_time')?.value || !c.get('end_time')?.value,
    );
  }

  /** Flags exact start/end duplicate time slots within the same day with a `duplicateTimeSlot` error. */
  private validateDuplicateServingTimeHours(day: string): void {
    const hours = this.getServingTimeHours(day);

    hours.controls.forEach((control) => {
      const errors = control.errors;
      if (errors && errors['duplicateTimeSlot']) {
        const newErrors: any = { ...errors };
        delete newErrors['duplicateTimeSlot'];
        control.setErrors(Object.keys(newErrors).length ? newErrors : null);
      }
    });

    for (let i = 0; i < hours.length; i++) {
      const current = hours.at(i) as FormGroup;
      const currentStart = current.get('start_time')?.value;
      const currentEnd = current.get('end_time')?.value;
      if (!currentStart || !currentEnd) {
        continue;
      }

      const hasDuplicate = hours.controls.some((other, j) => {
        if (j === i) {
          return false;
        }
        const otherStart = other.get('start_time')?.value;
        const otherEnd = other.get('end_time')?.value;
        return (
          otherStart === currentStart &&
          otherEnd === currentEnd &&
          !!other.get('next_day')?.value === !!current.get('next_day')?.value
        );
      });

      if (hasDuplicate) {
        current.setErrors({ ...(current.errors || {}), duplicateTimeSlot: true });
        current.markAsTouched();
        current.markAsDirty();
      }
    }
  }

  hasServingTimeDuplicateError(day: string, hourIndex: number): boolean {
    return !!this.getServingTimeHours(day).at(hourIndex)?.hasError('duplicateTimeSlot');
  }

  hasServingTimeDuplicates(day: string): boolean {
    return this.getServingTimeHours(day).controls.some((c) => c.hasError('duplicateTimeSlot'));
  }

  /**
   * Flags a slot with `overlappingTimeSlot` if its range overlaps — or exactly touches — another
   * slot's range for the same day (e.g. one ending 01:45 AM while another starts 01:45 AM is not
   * allowed; they must have a real gap or genuinely not touch at all).
   */
  private validateOverlappingServingTimeHours(day: string): void {
    const hours = this.getServingTimeHours(day);

    hours.controls.forEach((control) => {
      const errors = control.errors;
      if (errors && (errors['overlappingTimeSlot'] || errors['overlapsPreviousDay'])) {
        const newErrors: any = { ...errors };
        delete newErrors['overlappingTimeSlot'];
        delete newErrors['overlapsPreviousDay'];
        control.setErrors(Object.keys(newErrors).length ? newErrors : null);
      }
    });

    // The previous day's slots that close the next day run into this day until their end time.
    const previousDay = this.previousServingTimeDay(day);
    const previousDayRunOns = this.getOtherServingTimeSlots(previousDay.key, -1).filter(
      (s) => s.end >= this.minutesPerDay,
    );

    for (let i = 0; i < hours.length; i++) {
      const current = hours.at(i) as FormGroup;
      const currentSlot = this.servingSlotMinutes(current);
      if (!currentSlot) {
        continue;
      }

      const overlaps = hours.controls.some((other, j) => {
        const otherSlot = j === i ? null : this.servingSlotMinutes(other);
        return (
          !!otherSlot && currentSlot.start <= otherSlot.end && otherSlot.start <= currentSlot.end
        );
      });

      if (overlaps) {
        current.setErrors({ ...(current.errors || {}), overlappingTimeSlot: true });
        current.markAsTouched();
        current.markAsDirty();
      }

      const previousDayRunOn = previousDayRunOns.find(
        (s) => currentSlot.start <= s.end - this.minutesPerDay,
      );
      if (this.isServingTimeDayEnabled(day) && previousDayRunOn) {
        current.setErrors({
          ...(current.errors || {}),
          overlapsPreviousDay: {
            day: previousDay.label,
            until: this.convertTo12HourFormat(previousDayRunOn.end_time),
          },
        });
        current.markAsTouched();
        current.markAsDirty();
      }
    }
  }

  getServingTimePreviousDayOverlap(
    day: string,
    hourIndex: number,
  ): { day: string; until: string } | null {
    return this.getServingTimeHours(day).at(hourIndex)?.getError('overlapsPreviousDay') ?? null;
  }

  hasServingTimeOverlapError(day: string, hourIndex: number): boolean {
    return !!this.getServingTimeHours(day).at(hourIndex)?.hasError('overlappingTimeSlot');
  }

  hasServingTimeOverlaps(day: string): boolean {
    return this.getServingTimeHours(day).controls.some(
      (c) => c.hasError('overlappingTimeSlot') || c.hasError('overlapsPreviousDay'),
    );
  }

  hasServingTimeRangeError(day: string, hourIndex: number): boolean {
    return !!this.getServingTimeHours(day).at(hourIndex)?.hasError('timeRangeInvalid');
  }

  hasServingTimeRangeErrors(day: string): boolean {
    return this.getServingTimeHours(day).controls.some((c) => c.hasError('timeRangeInvalid'));
  }

  canRemoveServingTimeHour(day: string): boolean {
    return this.getServingTimeHours(day).length > 1;
  }

  private applyServingTimeDefaults(
    servingTime: { day: string; hours: { start_time: string; end_time: string }[] }[],
  ): void {
    const slotsByDay = new Map<
      string,
      { start_time: string; end_time: string; next_day: boolean }[]
    >();
    (servingTime || []).forEach((entry) => {
      if (!this.getServingTimeDayGroup(entry.day)) {
        return;
      }
      const hours =
        entry.hours && entry.hours.length ? entry.hours : [{ start_time: '', end_time: '' }];
      hours.forEach((hour) => {
        // A slot in the hours after midnight that the previous day's restaurant hours run
        // into is shown on the previous day, as its next-day slot.
        const previousDay = this.previousServingTimeDay(entry.day).key;
        const nextDay = this.isPreviousDayNextDaySlot(entry.day, hour);
        const day = nextDay ? previousDay : entry.day;
        slotsByDay.set(day, [...(slotsByDay.get(day) || []), { ...hour, next_day: nextDay }]);
      });
    });
    slotsByDay.forEach((hours, day) => {
      this.getServingTimeDayGroup(day).get('enabled')?.setValue(true);
      const hoursArray = this.getServingTimeHours(day);
      hoursArray.clear();
      hours.forEach((hour) =>
        hoursArray.push(this.createServingTimeHour(hour.start_time, hour.end_time, hour.next_day)),
      );
      this.validateDuplicateServingTimeHours(day);
      this.validateOverlappingServingTimeHours(day);
      this.validateServingTimeWithinRestaurantHours(day);
    });
    // Once every day is filled in, check each day against the previous day's next-day slots.
    this.servingTimeDays.forEach((d) => this.validateOverlappingServingTimeHours(d.key));
    // Binding a freshly built form re-runs its validators and drops the errors set by hand,
    // so check again once it has rendered.
    setTimeout(() => this.validateAllServingTimeDays());
  }

  /**
   * True when a saved slot falls in the hours after midnight that the previous day's
   * restaurant hours run into (Mon 8 PM – 4 AM covers a Tue 1 – 3 AM slot), and not in the
   * day's own restaurant hours.
   */
  private isPreviousDayNextDaySlot(
    day: string,
    hour: { start_time: string; end_time: string },
  ): boolean {
    const previousDay = this.previousServingTimeDay(day).key;
    if (!hour.start_time || !hour.end_time || !this.hasRestaurantHoursConstraint(previousDay)) {
      return false;
    }
    const start = this.timeToMinutes(hour.start_time);
    if (this.hasRestaurantHoursConstraint(day) && this.getRestaurantRangeContaining(day, start)) {
      return false;
    }
    const range = this.getRestaurantRangeContaining(previousDay, start + this.minutesPerDay);
    return (
      !!range &&
      start + this.minutesPerDay + this.servingSpanMinutes(hour.start_time, hour.end_time) <=
        range.end
    );
  }

  /** The duplicate, overlap and restaurant-hours checks, for every day. */
  private validateAllServingTimeDays(): void {
    this.servingTimeDays.forEach((d) => {
      this.validateDuplicateServingTimeHours(d.key);
      this.validateOverlappingServingTimeHours(d.key);
      this.validateServingTimeWithinRestaurantHours(d.key);
    });
  }

  /**
   * Reconciles serving_time once both the existing menu and the restaurant's hours have
   * (possibly independently, in either order) finished loading. An existing menu with no
   * serving_time of its own falls back to the restaurant's default hours instead of a
   * blank/all-unchecked form.
   */
  private refreshServingTimeFromLoadedData(): void {
    if (!this.exitingMenuId) {
      this.applyServingTimeDefaults(this.formattedRestaurantServingHours);
      return;
    }
    if (this.exitingMenuServingTime === undefined) {
      return; // menu hasn't loaded yet; nothing to reconcile
    }
    this.initServingTimeForm();
    if (this.exitingMenuServingTime.length > 0) {
      this.applyServingTimeDefaults(this.exitingMenuServingTime);
    } else {
      this.applyServingTimeDefaults(this.formattedRestaurantServingHours);
    }
  }

  getServingTimePayload(): { day: string; hours: { start_time: string; end_time: string }[] }[] {
    const hoursByDay = new Map<string, { start_time: string; end_time: string }[]>();
    this.servingTimeDays
      .filter((d) => this.isServingTimeDayEnabled(d.key))
      .forEach((d) =>
        this.getServingTimeHours(d.key).controls.forEach((c) => {
          const { start_time, end_time, next_day } = c.value;
          // Drop unfilled rows (e.g. a "+ Hours" row the user never set times for) —
          // never send empty-string start_time/end_time to the API.
          if (!start_time || !end_time) {
            return;
          }
          // A slot that starts after midnight is the next day's slot.
          const day = next_day ? this.nextServingTimeDay(d.key) : d.key;
          hoursByDay.set(day, [...(hoursByDay.get(day) || []), { start_time, end_time }]);
        }),
      );
    return this.servingTimeDays
      .filter((d) => hoursByDay.has(d.key))
      .map((d) => ({ day: d.key, hours: hoursByDay.get(d.key)! }));
  }

  ngOnInit() {
    // Read before anything else: stepping out to add a type or customization
    // parks the in-progress menu here, and the fetches below must not race it.
    const draft = this.readDraft();

    this.route.paramMap.subscribe((params) => {
      this.exitingMenuId = params.get('id');
    });

    this.restaurantService.restaurantPosProvider$.subscribe((posProvider: any) => {
      this.restaurantPosProvider = posProvider;
      this.isNotFnfHardware = isPosManaged(this.restaurantPosProvider);
      if (isPosManaged(this.restaurantPosProvider) && !this.exitingMenuId) {
        this.router.navigate(['/']);
      }
    });

    if (
      this.exitingMenuId &&
      this.exitingMenuId != '' &&
      this.exitingMenuId != null &&
      this.exitingTypes
    ) {
      this.displayLoading = true;
      this.menuService.getMenuById(this.exitingMenuId).subscribe({
        next: (response: FNFResponse<any>) => {
          if (!response.data && response.error) {
            this.toastr.error(response.error.error?.message || response.error.message, 'Error');
            this.displayLoading = false;
            return;
          } else if (!response.data) {
            this.toastr.error('No data found!', 'Error');
            this.displayLoading = false;
            return;
          }
          this.displayLoading = false;
          this.exitingMenu = response.data;
          this.productForm.patchValue({
            name: response.data.name,
            description: response.data.description,
            coverImage: [null],
            image_url: response.data.image_url,
            originalPrice: response.data.price,
            offerPrice: response.data.promotion_price,
            sku: response.data.sku,
            stockStatus: response.data.is_available,
            preparationTime: response.data.preparation_time,
            servingSize: response.data.serving_size,
            additionalNotes: response.data.notes,
            status: response.data.status,
            // servingTime: response.data.serving_time && response.data.serving_time.id ? response.data.serving_time.id : 0
          });
          this.exitingMenuServingTime = response.data.serving_time || [];
          this.refreshServingTimeFromLoadedData();
          this.selectedStation = this.stations.find(
            (s: any) => s.station_id == this.exitingMenu.station_id,
          );
          this.productForm.get('sku')?.setValue(response.data.sku);
          // Menus saved before these flags existed default to dine-in + togo.
          this.orderTypes.patchValue({
            is_dine_in: response.data.is_dine_in ?? true,
            is_togo: response.data.is_togo ?? true,
            is_catering: response.data.is_catering ?? false,
          });
          this.tagsList = response.data.tags;
          if (this.tagsList && this.tagsList.length > 0) {
            this.tagsList.forEach((tag, i) => {
              this.addTags(i + 1, tag);
            });
          }

          this.menuIngredients = response.data.ingredients;
          if (this.menuIngredients && this.menuIngredients.length > 0) {
            this.menuIngredients.forEach((ingredient) => {
              this.addIngredients(ingredient, ingredient);
            });
          }

          let defaults = Object.entries(response.data.defaults);
          if (defaults && defaults.length > 0) {
            defaults.forEach((e: any, i: any) => {
              this.addCharacteristics(i + 1, e[0], e[1]);
            });
          }

          let customizations = response.data.customizations;
          if (customizations && customizations.length > 0) {
            customizations.forEach((e: any) => {
              let options: any = [];
              if (e.default && Array.isArray(e.default)) {
                e.types.forEach((t: any) => {
                  let option = {
                    id: t.type_id,
                    name: t.value,
                    isChecked: t.is_default,
                    isEditable: !e.is_price_different,
                  };
                  options.push(option);
                });
                this.addCustomization1(
                  e.id,
                  e.name,
                  'Multi Selection',
                  options,
                  [],
                  !e.is_price_different,
                );
                // defer selection sync until both data sources are ready
              } else {
                e.types.forEach((t: any) => {
                  let option = {
                    id: t.type_id,
                    name: t.value,
                    isChecked: t.is_default,
                    isEditable: !e.is_price_different,
                  };
                  options.push(option);
                });
                this.addCustomization1(
                  e.id,
                  e.name,
                  'Single Selection',
                  options,
                  [],
                  !e.is_price_different,
                );
              }
            });
            setTimeout(() => {
              this.syncSelectedCustomizationsFromFormArray();
            }, 1000);
          }

          let additional_images = response.data.additional_images;
          if (additional_images && additional_images.length > 0) {
            // Limit to maximum 4 images
            const imagesToLoad = additional_images.slice(0, 4);
            imagesToLoad.forEach((e: any) => {
              this.additionalImages.push(this.fb.control(e));
            });
            // Also populate the images array for display
            imagesToLoad.forEach((e: any) => {
              this.images.push({
                file: null,
                url: e,
              });
            });
          }

          let classifications = response.data.types;
          this.existingCustomization = classifications;
          if (this.isCustomizationLoaded) {
            this.setExitingCustomization();
          }

          // The draft holds newer edits than the saved menu, so it wins.
          if (draft) {
            this.applyDraft(draft);
          }
        },
        error: (error) => {
          this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
          this.displayLoading = false;
        },
      });
    }
  }

  skuPatternValidator() {
    return (control: any) => {
      const pattern = /^[a-zA-Z]{2}[0-9]{3}$/;
      if (control.value && !pattern.test(control.value)) {
        return { invalidSku: true };
      }
      return null;
    };
  }

  // Mirrors the restaurant-service rule that rejects menu names made only of digits.
  notNumbersOnlyValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = typeof control.value === 'string' ? control.value.trim() : '';
      return /^\d+$/.test(value) ? { numbersOnly: true } : null;
    };
  }

  preventNegativeInput(event: KeyboardEvent) {
    const invalidChars = ['-', '+', 'e', 'E'];
    if (invalidChars.includes(event.key)) {
      event.preventDefault();
    }
  }

  skuExistingValidator() {
    return (control: any) => {
      if (control.value && this.exitingSKUs.find((f: any) => f == control.value)) {
        if (this.exitingMenuId && this.exitingMenu && control.value == this.exitingMenu.sku) {
          return null;
        }
        return { invalidExistingSku: true };
      }
      return null;
    };
  }

  addTag(label: any) {
    const tag = document.createElement('div');
    tag.className = 'tag1';
    tag.textContent = label;

    // Add remove "X" button to tag
    const closeButton = document.createElement('span');
    closeButton.textContent = '✖';
    closeButton.onclick = () => tag.remove();

    tag.appendChild(closeButton);
    this.tagInput.insertBefore(tag, this.inputField);
  }

  addIngredient() {
    if (
      this.productForm.get('ingredientName')?.value.trim() &&
      !this.menuIngredients.includes(this.productForm.get('ingredientName')?.value.trim())
    ) {
      this.menuIngredients.push(this.productForm.get('ingredientName')?.value.trim());
      this.addIngredients(
        this.productForm.get('ingredientName')?.value.trim(),
        this.productForm.get('ingredientName')?.value.trim(),
      );
    }
    this.productForm.get('ingredientName')?.reset();
    this.newIngredient = '';
  }

  removeIngredient(index: number) {
    this.menuIngredients.splice(index, 1);
    this.ingredients.removeAt(index);
  }

  addNewTag() {
    if (
      this.productForm.get('tagName')?.value.trim() &&
      !this.tagsList.includes(this.productForm.get('tagName')?.value.trim())
    ) {
      this.addTags(this.tagsList.length + 1, this.productForm.get('tagName')?.value.trim());
      this.tagsList.push(this.productForm.get('tagName')?.value.trim());
    }
    this.productForm.get('tagName')?.reset();
    // this.newTag = '';
  }

  removeTag(index: number) {
    this.tagsList.splice(index, 1);
    this.tags.removeAt(index);
  }

  // Handle file selection and preview
  onFileSelected(event: Event): void {
    const fileInput = event.target as HTMLInputElement;

    if (fileInput?.files && fileInput.files.length > 0) {
      const file = fileInput.files[0];

      // The 1MB upload limit is checked after crop + compression
      const sourceError = sourceImageError(file);
      if (sourceError) {
        this.coverImageSizeError = sourceError;
        this.productForm.get('coverImage')?.setValue(null);
        return;
      }

      this.coverImageSizeError = '';
      this.uploadedFile = file;

      // Open crop modal
      const reader = new FileReader();
      reader.onload = () => {
        const imageSrc = reader.result as string;
        this.openCropModal(imageSrc);
      };
      reader.readAsDataURL(file);
    }
  }

  openCropModal(imageSrc: string): void {
    const dialogRef = this.dialog.open(ImageCropingComponent, {
      width: '700px',
      maxWidth: '90vw',
      disableClose: true,
      data: {
        imageSrc: imageSrc,
        targetWidth: 402,
        targetHeight: 201,
        defaultAspectRatio: 'original',
        title: 'Crop Menu Cover Image',
        isImageFrom: 'menu-cover',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      const uploadError = result?.blob ? uploadSizeError(result.blob) : null;
      if (result && result.croppedImage && !uploadError) {
        this.imagePreview = result.croppedImage;

        const file = croppedImageFile(result.blob, 'menu-cover');
        this.uploadedFile = file;

        // Update the form control
        this.menuForm.patchValue({
          image: file,
        });

        this.productForm.get('coverImage')?.markAsTouched();
        this.setCoverImageFormData(file, 'cover', true);
      } else {
        if (uploadError) {
          this.coverImageSizeError = uploadError;
        }
        // User cancelled or the result is too large, reset file input
        const fileInput = document.getElementById('fileUpload') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
        this.uploadedFile = null;
      }
    });
  }

  onSubmit(): void {
    if (this.menuForm.valid && this.uploadedFile) {
      // Reset the form and preview
      this.menuForm.reset();
      this.imagePreview = null;
      this.uploadedFile = null;

      alert('Image uploaded successfully!');
    } else {
      alert('Please select an image before submitting.');
    }
  }

  cancelMenu() {
    if (this.productForm.dirty && this.productForm.touched) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          message: 'This will permanently discard latest changes and you can’t undo this later.',
          buttonText: {
            ok: 'Yes, Discard',
            cancel: 'No, Cancel',
          },
        },
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          if (this.exitingMenuId) {
            this.router.navigate(['main/menu']);
          } else {
            window.location.reload();
          }
        }
      });
    } else if (this.exitingMenuId) {
      this.router.navigate(['main/menu']);
    }
  }

  archivedMenu() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: 'This will permanently delete this menu and you can’t undo this later.',
        buttonText: {
          ok: 'Yes, Delete',
          cancel: 'No, Cancel',
        },
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.createNewMenu('archived');
      }
    });
  }

  // Classification checkboxes sync from an async call separate from the menu load, so on an
  // edit page they can still be unchecked at click-time even though the menu already has a
  // saved category — fall back to the loaded menu's own types in that case.
  private computeIsAnyCategorySelected(): boolean {
    const hasCheckedOption = this.classification.value.some((e: any) =>
      e.options.some((f: any) => f.isChecked),
    );
    if (hasCheckedOption) {
      return true;
    }
    return (
      !!this.exitingMenuId &&
      (this.exitingMenu?.types || []).some((t: any) => t.categories && t.categories.length > 0)
    );
  }

  publishedMenu() {
    this.selectedStatus = 'published';
    this.isSubmit = true;
    this.isAnyCategorySelected = this.computeIsAnyCategorySelected();
    if (this.hasServingTimeErrors()) {
      return;
    }
    if (
      this.productForm.valid &&
      this.productForm.get('status')?.value != 'published' &&
      this.isAnyCategorySelected
    ) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          message: 'This will directly publish this menu to restaurant.',
          buttonText: {
            ok: 'Yes, Publish',
            cancel: 'No, Cancel',
          },
        },
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.createNewMenu('published');
        }
      });
    } else if (this.productForm.valid && this.productForm.get('status')?.value == 'published') {
      this.createNewMenu('published');
    }
  }

  draftMenu() {
    this.selectedStatus = 'draft';
    this.isSubmit = true;
    this.isAnyCategorySelected = this.computeIsAnyCategorySelected();
    if (this.hasServingTimeErrors()) {
      return;
    }
    // Drafts skip full form validation, so block the name rule the API would reject anyway.
    if (this.productForm.get('name')?.hasError('numbersOnly')) {
      this.productForm.get('name')?.markAsTouched();
      return;
    }
    if (this.productForm.get('status')?.value == 'published') {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          message: 'This will not populate the menu in the restaurant.',
          buttonText: {
            ok: 'Yes',
            cancel: 'No, Cancel',
          },
        },
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.createNewMenu('draft');
        }
      });
    } else {
      this.createNewMenu('draft');
    }
  }

  /** Returns true (and shows a toast) if any day's serving-time schedule has an error. */
  private hasServingTimeErrors(): boolean {
    this.validateAllServingTimeDays();

    const dayWithDuplicates = this.servingTimeDays.find((d) =>
      this.hasServingTimeDuplicates(d.key),
    );
    if (dayWithDuplicates) {
      this.toastr.error(
        `Duplicate time slots found for ${dayWithDuplicates.label}. Please remove or update them.`,
        'Error',
      );
      return true;
    }

    const dayWithOverlaps = this.servingTimeDays.find((d) => this.hasServingTimeOverlaps(d.key));
    if (dayWithOverlaps) {
      this.toastr.error(
        `Overlapping time slots found for ${dayWithOverlaps.label}. Please fix the times so they don't overlap.`,
        'Error',
      );
      return true;
    }

    const dayWithRangeErrors = this.servingTimeDays.find((d) =>
      this.hasServingTimeRangeErrors(d.key),
    );
    if (dayWithRangeErrors) {
      this.toastr.error(
        `On ${dayWithRangeErrors.label}, end time must be different from start time.`,
        'Error',
      );
      return true;
    }

    const dayOutsideRestaurantHours = this.servingTimeDays.find((d) =>
      this.hasServingTimeOutsideRestaurantHours(d.key),
    );
    if (dayOutsideRestaurantHours) {
      this.toastr.error(
        `On ${dayOutsideRestaurantHours.label}, serving time must be within the restaurant's open hours.`,
        'Error',
      );
      return true;
    }

    const dayWithIncompleteHours = this.servingTimeDays.find((d) =>
      this.hasServingTimeIncompleteHours(d.key),
    );
    if (dayWithIncompleteHours) {
      this.toastr.error(
        `On ${dayWithIncompleteHours.label}, please fill in or remove the incomplete time slot.`,
        'Error',
      );
      return true;
    }

    return false;
  }

  createNewMenu(status?: any) {
    this.isSubmit = true;
    this.selectedStatus = status;

    let menuPayload = {
      name: this.productForm.get('name')?.value,
      description: this.productForm.get('description')?.value,
      price: this.productForm.get('originalPrice')?.value,
      promotion_price: this.productForm.get('offerPrice')?.value,
      sku: this.productForm.get('sku')?.value || null,
      currency: 'USD',
      is_available: this.productForm.get('stockStatus')?.value,
      image_url: this.productForm.get('image_url')?.value,
      customizations: this.getCustomizations(),
      types: this.getTypes(),
      ingredients: this.getIngredients(),
      preparation_time: this.productForm.get('preparationTime')?.value,
      serving_size: this.productForm.get('servingSize')?.value,
      defaults: this.getCharacteristics(),
      tags: this.getTags(),
      notes: this.productForm.get('additionalNotes')?.value,
      additional_images: this.productForm.get('additional_images')?.value,
      status: status,
      // "serving_time_id": this.productForm.get('servingTime')?.value
      serving_time: this.getServingTimePayload(),
      station_id: this.selectedStation ? this.selectedStation.station_id : null,
      is_dine_in: !!this.productForm.get('orderTypes.is_dine_in')?.value,
      is_togo: !!this.productForm.get('orderTypes.is_togo')?.value,
      is_catering: !!this.productForm.get('orderTypes.is_catering')?.value,
    };

    this.isAnyCategorySelected = this.computeIsAnyCategorySelected();

    if (status == 'draft' && this.productForm.get('name')?.value && this.isAnyCategorySelected) {
      if (this.exitingMenuId) {
        this.editMenu(menuPayload);
      } else {
        this.addMenu(menuPayload);
      }
    } else if (this.productForm.valid && this.isAnyCategorySelected) {
      if (this.exitingMenuId && isFnfLike(this.restaurantPosProvider)) {
        this.editMenu(menuPayload);
      } else if (this.exitingMenuId && isPosManaged(this.restaurantPosProvider)) {
        this.patchMenu(menuPayload);
      } else {
        this.addMenu(menuPayload);
      }
    } else {
    }
  }

  // A 422 carries the field-level reason in error.detail[].msg; prefer it over the generic message.
  private getApiErrorMessage(apiError: any): string {
    const detailMsg = apiError?.error?.detail?.[0]?.msg;
    if (typeof detailMsg === 'string' && detailMsg.trim()) {
      return detailMsg.replace(/^Value error,\s*/i, '');
    }
    return apiError?.error?.message || apiError?.message;
  }

  addMenu(menu: any) {
    this.ngxSpinnerService.show();
    this.menuService.addMenu(menu).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(this.getApiErrorMessage(response.error), 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        this.ngxSpinnerService.hide();
        this.router.navigate(['main/menu']);
      },
      error: (error) => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  editMenu(menu: any) {
    menu.menu_id = this.exitingMenuId;
    if (localStorage.getItem('user')) {
      let user: any = localStorage.getItem('user');
      if (user) {
        let jsonUser = JSON.parse(user);
        menu.restaurant_id = jsonUser.restaurant_id;
      }
    }

    this.ngxSpinnerService.show();
    this.menuService.editMenu(menu).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(this.getApiErrorMessage(response.error), 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }

        this.ngxSpinnerService.hide();
        this.productForm.get('status')?.setValue(response.data.status);
        this.toastr.success('Menu updated', 'Success');
        this.router.navigate(['main/menu']);
      },
      error: (error) => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  patchMenu(menu: any) {
    menu.menu_id = this.exitingMenuId;
    if (localStorage.getItem('user')) {
      let user: any = localStorage.getItem('user');
      if (user) {
        let jsonUser = JSON.parse(user);
        menu.restaurant_id = jsonUser.restaurant_id;
      }
    }

    this.ngxSpinnerService.show();
    this.menuService.patchMenu(menu).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(this.getApiErrorMessage(response.error), 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }

        this.ngxSpinnerService.hide();
        this.productForm.get('status')?.setValue(response.data.status);
        this.toastr.success('Menu updated', 'Success');
        this.router.navigate(['main/menu']);
      },
      error: (error) => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  getCustomizations() {
    let customizations: any = [];
    this.customization.value.forEach((e: any) => {
      let options: any = [];
      let existingCustomizationType = this.customizations
        .find((f: any) => f.id == e.id)
        ?.types.filter((f: any) => f.is_default)
        .map((f: any) => f.type_id);
      let localIsOptionsChecked = e.options
        .filter((f: any) => f.isChecked)
        .map((f: any) => f.type_id);

      if (!e.isEditable) {
        options = [];
      } else if (e.inputType != 'Multi Selection') {
        if (
          existingCustomizationType &&
          existingCustomizationType.length > 0 &&
          existingCustomizationType[0] == localIsOptionsChecked[0]
        ) {
          options = [];
        } else {
          options = [localIsOptionsChecked[0]];
        }
      } else if (e.inputType == 'Multi Selection') {
        // existingCustomizationType, localIsOptionsChecked have same elements then options = [];
        // otherwise options = localIsOptionsChecked
        if (
          existingCustomizationType.every((f: any) => localIsOptionsChecked.includes(f)) &&
          localIsOptionsChecked.every((f: any) => existingCustomizationType.includes(f))
        ) {
          options = [];
        } else {
          options = localIsOptionsChecked;
        }
      }
      // let isCheckedValue = '';
      // let isCheckedValues:any = [];
      // e.options.forEach((f:any) => {
      //   if(f.isChecked && e.isEditable) {
      //     options.push(f.type_id);
      //   }
      //   // options.push({
      //   //   value: f.name,
      //   //   price: f.price ? f.price : 0
      //   // });
      //   if(f.isChecked && e.inputType != 'Multi Selection') {
      //     isCheckedValue = f.name;
      //   }
      //   else if(f.isChecked && e.inputType == 'Multi Selection') {
      //     isCheckedValues.push(f.name);
      //   }
      // });
      if (!customizations.find((f: any) => f.id == e.id)) {
        customizations.push({
          id: e.id,
          menu_defaults: options,
          // "name": e.name,
          // "types": options,
          // "remarks": "",
          //"default": isCheckedValue
          // "default": e.inputType == 'Multi Selection' ?  isCheckedValues : isCheckedValue
        });
      }
    });

    return customizations;
  }

  getTypes() {
    let types: any = [];

    this.classification.value.forEach((e: any) => {
      let categories: any = [];
      e.options.forEach((f: any) => {
        if (f.isChecked) {
          categories.push({
            id: f.id,
            name: f.name,
          });
        }
      });

      types.push({
        id: e.id,
        name: e.name,
        categories: categories,
      });
    });
    return types;
  }

  getTags() {
    let tags: any = [];
    if (this.tags.value.length > 0) {
      (this.productForm.get('tags') as FormArray).value.forEach((e: any) => {
        tags.push(e.name);
      });
    }
    return tags;
  }

  getIngredients() {
    let ingredients: any = [];
    if (this.ingredients.value.length > 0) {
      (this.productForm.get('ingredients') as FormArray).value.forEach((e: any) => {
        ingredients.push(e.name);
      });
    }
    return ingredients;
  }

  getCharacteristicsArray() {
    let characteristics: any = [];
    if (this.characteristics.value.length > 0) {
      (this.productForm.get('characteristics') as FormArray).value.forEach((e: any) => {
        characteristics.push({ name: e.name, value: e.value });
      });
    }
    return characteristics;
  }

  getCharacteristics() {
    let defaults: { [key: string]: string } = this.createDefaults(this.characteristics.value);
    return defaults;
  }

  createDefaults(characteristics: { name: string; value: string }[]): { [key: string]: string } {
    return characteristics.reduce(
      (defaults, item) => {
        defaults[item.name] = item.value; // Map the name as key and value as value
        return defaults;
      },
      {} as { [key: string]: string },
    );
  }

  onStatusChange(event: Event) {
    const value = (event.target as HTMLSelectElement).value;
    this.selectedStatus = value;
    this.productForm.get('status')?.setValue(value);
  }

  removeImage() {
    this.uploadedFile = null;
    this.imagePreview = null;
    this.productForm.get('coverImage')?.setValue(null);
    this.productForm.get('image_url')?.setValue('');
    this.coverImageSizeError = '';
  }

  removeAdditionalImage(index: number): void {
    if (index >= 0 && index < this.images.length) {
      this.images.splice(index, 1);
    }
    if (index >= 0 && index < this.additionalImages.length) {
      this.additionalImages.removeAt(index);
    }
    this.additionalImageSizeError = '';
  }

  onFilesSelected(event: any): void {
    const files: FileList = event.target.files;
    const currentImageCount = this.additionalImages.length;

    if (currentImageCount + files.length > 4) {
      this.toastr.error('You can only upload up to 4 images.', 'Error');
      // Reset file input
      const fileInput = event.target as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
      return;
    }

    this.additionalImageSizeError = '';
    const fileArray = Array.from(files);

    // Process files sequentially through crop modal
    this.processAdditionalImagesSequentially(fileArray, 0);
  }

  processAdditionalImagesSequentially(files: File[], currentIndex: number): void {
    if (currentIndex >= files.length) {
      // All files processed
      return;
    }

    const file = files[currentIndex];

    // The 1MB upload limit is checked after crop + compression
    const sourceError = sourceImageError(file);
    if (sourceError) {
      alert(`${file.name}: ${sourceError}`);
      this.additionalImageSizeError = sourceError;
      // Continue with next file
      this.processAdditionalImagesSequentially(files, currentIndex + 1);
      return;
    }

    // Read file and open crop modal
    const reader = new FileReader();
    reader.onload = () => {
      const imageSrc = reader.result as string;
      this.openAdditionalImageCropModal(imageSrc, file.name, currentIndex, files);
    };
    reader.readAsDataURL(file);
  }

  openAdditionalImageCropModal(
    imageSrc: string,
    fileName: string,
    currentIndex: number,
    files: File[],
  ): void {
    const dialogRef = this.dialog.open(ImageCropingComponent, {
      width: '700px',
      maxWidth: '90vw',
      disableClose: true,
      data: {
        imageSrc: imageSrc,
        targetWidth: 402,
        targetHeight: 201,
        defaultAspectRatio: 'original',
        title: `Crop Menu Additional Image`,
        isImageFrom: 'menu-additional-image',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.croppedImage) {
        // Check if we've reached the maximum limit before adding
        if (this.additionalImages.length >= 4) {
          this.toastr.error('You can only upload up to 4 images.', 'Error');
          // Process next file (skip this one)
          this.processAdditionalImagesSequentially(files, currentIndex + 1);
          return;
        }

        const uploadError = uploadSizeError(result.blob);
        if (uploadError) {
          this.additionalImageSizeError = uploadError;
          this.processAdditionalImagesSequentially(files, currentIndex + 1);
          return;
        }

        // Named after the real output type, not the source file's extension
        const baseName = fileName.replace(/\.[^.]+$/, '');
        const croppedFile = croppedImageFile(result.blob, `cropped-${baseName}`);

        // Add to images array
        this.images.push({
          file: croppedFile,
          url: result.croppedImage,
        });

        // Upload the cropped image
        this.setCoverImageFormData(croppedFile, 'additional_' + (this.images.length - 1), false);

        // Process next file
        this.processAdditionalImagesSequentially(files, currentIndex + 1);
      } else {
        // User cancelled, process next file
        this.processAdditionalImagesSequentially(files, currentIndex + 1);
      }
    });
  }

  get additionalImages() {
    return this.productForm.get('additional_images') as FormArray;
  }

  get classification() {
    return this.productForm.get('classification') as FormArray;
  }

  get orderTypes() {
    return this.productForm.get('orderTypes') as FormGroup;
  }

  private readonly lowStockThreshold = 5;

  /** Per-variation stock, as synced from Square (one row per size/option, e.g. Small/Medium/Large). */
  get squareVariationStocks(): { name: string; stockCount: number }[] {
    const variations = this.exitingMenu?.external_references?.square?.variations || [];
    return variations
      .map((v: any) => ({ name: v.name, stockCount: this.parseStockCount(v.stock_count) }))
      .filter((v: any): v is { name: string; stockCount: number } => v.stockCount !== null);
  }

  /** Whether the item has more than one Square variation (e.g. Small/Medium/Large), regardless of how many report stock. */
  get hasMultipleSquareVariations(): boolean {
    const variations = this.exitingMenu?.external_references?.square?.variations || [];
    return variations.length > 1;
  }

  private parseStockCount(stockCount: any): number | null {
    if (stockCount === null || stockCount === undefined || stockCount === '') {
      return null;
    }
    const parsed = Number(stockCount);
    return Number.isNaN(parsed) ? null : parsed;
  }

  stockBadgeClass(stockCount: number): string {
    if (stockCount <= 0) {
      return 'stock-badge-out';
    }
    return stockCount <= this.lowStockThreshold ? 'stock-badge-low' : 'stock-badge-ok';
  }

  stockBadgeLabel(stockCount: number): string {
    if (stockCount <= 0) {
      return 'Out of stock';
    }
    return stockCount <= this.lowStockThreshold
      ? `Low · ${stockCount} left`
      : `${stockCount} in stock`;
  }

  atLeastOneOrderTypeValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const fg = group as FormGroup;
      const hasSelection = Object.values(fg.controls).some((c) => c.value === true);
      return hasSelection ? null : { atLeastOneRequired: true };
    };
  }

  onOrderTypeChange(field: string): void {
    // Unchecking the last remaining order type would leave the menu orderable
    // nowhere, so put the one just unchecked back.
    const group = this.orderTypes;
    const anySelected = Object.values(group.controls).some((c) => c.value === true);
    if (!anySelected) {
      group.get(field)?.setValue(true, { emitEvent: false });
    }
  }

  getOption(classification: any) {
    return classification.controls.options as FormArray;
    //return <FormArray>classification.controls.options.controls;
  }

  getSubOptions(index: number): FormArray {
    return (this.classification.at(index) as FormGroup).get('options') as FormArray;
  }

  get customization() {
    return this.productForm.get('customization') as FormArray;
  }

  getCustomizationOption(customization: any) {
    const optionsControl = customization.get('options');
    return optionsControl ? (optionsControl as FormArray) : this.fb.array([]);
  }

  get characteristics() {
    return this.productForm.get('characteristics') as FormArray;
  }

  get ingredients() {
    return this.productForm.get('ingredients') as FormArray;
  }

  get tags() {
    return this.productForm.get('tags') as FormArray;
  }

  // Helper Methods to Add Form Controls
  addAdditionalImage(image: string) {
    this.additionalImages.push(this.fb.control(image));
  }

  ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  addClassification(
    id: number,
    name: string,
    isMultiSelect: boolean,
    options: any[],
    isMandatory: boolean,
  ) {
    const classificationGroup = this.fb.group(
      {
        id: [id, Validators.required],
        name: [name, Validators.required],
        isMultiSelect: [isMultiSelect, Validators.required],
        isMandatory: [isMandatory],
        options: this.fb.array(
          options.map((option) =>
            this.fb.group({
              id: [option.id, Validators.required],
              name: [option.name, Validators.required],
              isChecked: [option.isChecked],
            }),
          ),
        ),
      },
      { validators: this.mandatoryClassificationValidator() },
    );

    this.classification.push(classificationGroup);
    this.isCustomizationLoaded = true;
    if (this.existingCustomization) {
      this.setExitingCustomization();
    }
  }

  mandatoryClassificationValidator() {
    return (formGroup: FormGroup) => {
      const isMandatory = formGroup.get('isMandatory')?.value;
      const options = formGroup.get('options') as FormArray;

      if (isMandatory && options) {
        const hasCheckedOption = options.controls.some(
          (option) => option.get('isChecked')?.value === true,
        );

        if (!hasCheckedOption) {
          return { mandatoryClassificationRequired: true };
        }
      }

      return null;
    };
  }

  setExitingCustomization() {
    this.classification.value.forEach((type: any, i: any) => {
      if (type.options && type.options.length > 0) {
        type.options.forEach((o: any, j: any) => {
          const options = this.classification.controls[i].get('options') as FormArray;
          let findType = this.existingCustomization.find((f: any) => f.name == type.name);
          let findOption = findType?.categories.find((f: any) => f.name == o.name);
          if (findOption) {
            options.controls[j].patchValue({ isChecked: true });
          }
        });
        // Trigger validation on the classification form group after setting options
        this.classification.controls[i].updateValueAndValidity();
      }
    });
  }

  // Visibility state comes from the customizations list (is_customer_visible),
  // not the menu form array; missing/undefined means visible.
  isCustomizationHiddenFromCustomers(id: any): boolean {
    const match = this.customizations?.find((c: any) => c.id === id);
    return match?.is_customer_visible === false;
  }

  addCustomization(id: number, name: string, option: string, isDefault: boolean) {
    this.customization.push(
      this.fb.group({
        id: [id, Validators.required],
        name: [name, Validators.required],
        option: [option, Validators.required],
        isDefault: [isDefault, Validators.required],
      }),
    );
  }

  addCustomization1(
    id: number,
    name: string,
    inputType: string,
    options: any[],
    optionsWithPrice?: any[],
    isEditable: boolean = false,
  ) {
    if (optionsWithPrice && optionsWithPrice.length > 0) {
      this.customization.push(
        this.fb.group({
          id: [id, Validators.required],
          name: [name, Validators.required],
          inputType: [inputType],
          isEditable: [isEditable],
          options: this.fb.array(
            options.map((option) =>
              this.fb.group({
                type_id: [option.id, Validators.required],
                name: [option.name, Validators.required],
                isChecked: [option.isChecked],
                price: [option.price],
                isEditable: [option.isEditable],
              }),
            ),
          ),
        }),
      );
    } else {
      this.customization.push(
        this.fb.group({
          id: [id, Validators.required],
          name: [name, Validators.required],
          inputType: [inputType],
          isEditable: [isEditable],
          options: this.fb.array(
            options.map((option) =>
              this.fb.group({
                type_id: [option.id, Validators.required],
                name: [option.name, Validators.required],
                isChecked: [option.isChecked],
                isEditable: [option.isEditable],
              }),
            ),
          ),
        }),
      );
    }
  }

  addCharacteristics(id: number, name: string, value: string) {
    this.characteristics.push(
      this.fb.group({
        id: [id, Validators.required],
        name: [name, Validators.required],
        value: [value, Validators.required],
      }),
    );
  }

  addIngredients(id: string, name: string) {
    this.ingredients.push(
      this.fb.group({
        id: [id, Validators.required],
        name: [name, Validators.required],
      }),
    );
  }

  addTags(id: number, name: string) {
    this.tags.push(
      this.fb.group({
        id: [id, Validators.required],
        name: [name, Validators.required],
      }),
    );
  }

  /**
   * Runs one AI generation with only that field marked busy. The flag clears on
   * every outcome (finalize), and the request is cancelled if the page is left
   * so a late response never patches a form that is gone.
   */
  private runGeneration(
    key: keyof AddMenuComponent['generating'],
    request: Observable<FNFResponse<any>>,
    onData: (data: any) => void,
  ) {
    this.generating[key] = true;
    request
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.generating[key] = false)),
      )
      .subscribe({
        next: (response: FNFResponse<any>) => {
          if (!response.data && response.error) {
            this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          } else if (!response.data) {
            this.toastr.error('No data found!', 'Error');
          } else {
            onData(response.data);
          }
        },
        error: () => {
          this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        },
      });
  }

  getMenuIngredients() {
    if (this.generating.ingredients) return;
    const name = this.productForm.get('name')?.value;
    this.runGeneration('ingredients', this.menuService.getIngredients(name), (data) => {
      const rawIngredients: string[] = data.ingredients || [];
      // Same failure mode as tag generation: when the AI needs more details
      // it replies with a clarifying sentence, which the backend's
      // comma-list parser chops into fragments instead of real ingredients.
      const looksLikeClarifyingMessage = rawIngredients.some(
        (ingredient) => ingredient.length > 30 || /[.!?]\s*$/.test(ingredient.trim()),
      );
      if (looksLikeClarifyingMessage || rawIngredients.length === 0) {
        this.toastr.info(
          rawIngredients.join(' ').trim() ||
            'Add a menu description first so we can suggest ingredients.',
          'More details needed',
        );
        return;
      }
      this.menuIngredients = rawIngredients;
      this.menuIngredients.forEach((ingredient) => {
        this.addIngredients(ingredient, ingredient);
      });
      this.productForm.get('ingredientName')?.markAsTouched();
    });
  }

  getMenuTags() {
    if (this.generating.tags) return;
    const name = this.productForm.get('name')?.value;
    this.runGeneration('tags', this.menuService.getTags(name), (data) => {
      const rawTags: string[] = data.tags || [];
      // The AI can't always produce real tags from just a dish name — when it
      // needs more details it replies with a clarifying sentence instead, which
      // the backend's comma-list parser then chops into sentence fragments
      // (e.g. "Cuisine Type", "Or Any Specific Characteristics Of The Dish.").
      // Treat any long or sentence-punctuated fragment as that message rather
      // than a real tag, so it's shown as a notice instead of broken chips.
      const looksLikeClarifyingMessage = rawTags.some(
        (tag) => tag.length > 30 || /[.!?]\s*$/.test(tag.trim()),
      );
      if (looksLikeClarifyingMessage || rawTags.length === 0) {
        this.toastr.info(
          rawTags.join(' ').trim() ||
            'Add a menu description first so we can suggest relevant tags.',
          'More details needed',
        );
        return;
      }
      this.tagsList = rawTags;
      this.tagsList.forEach((tag) => {
        this.addTags(1, tag);
      });
      this.productForm.get('tagName')?.markAsTouched();
    });
  }

  getMenuPreparationTime() {
    if (this.generating.prepTime) return;
    const control = this.productForm.get('preparationTime');
    const valueAtStart = control?.value;
    const name = this.productForm.get('name')?.value;
    const description = this.productForm.get('description')?.value || '';
    this.runGeneration(
      'prepTime',
      this.menuService.getPreparationTime(name, description),
      (data) => {
        const minutes = data.preparation_time;
        // The field takes 5-180 whole minutes; anything else is a bad reply,
        // never something to put in the form.
        if (!Number.isInteger(minutes) || minutes < 5 || minutes > 180) {
          this.toastr.error('Could not estimate a preparation time. Please enter one.', 'Error');
          return;
        }
        // The form stays editable during generation; never overwrite a value
        // the user typed after pressing Generate.
        if (control?.value !== valueAtStart) {
          this.toastr.info('Kept the preparation time you typed.', 'Generated value not applied');
          return;
        }
        control?.setValue(minutes);
        control?.markAsTouched();
        control?.markAsPristine();
      },
    );
  }

  getMenuDescriptions() {
    if (this.generating.description) return;
    const description = this.productForm.get('description');
    const valueAtStart = description?.value;
    const name = this.productForm.get('name')?.value;
    this.runGeneration('description', this.menuService.getDescription(name), (data) => {
      // The form stays editable during generation; never overwrite text the
      // user typed into the description after pressing Generate.
      if (description?.value !== valueAtStart) {
        this.toastr.info('Kept the description you typed.', 'Generated text not applied');
        return;
      }
      description?.setValue(data.dish_description);
      description?.markAsTouched();
      description?.markAsPristine();
    });
  }

  setCoverImageFormData(file: File, fileName: string, isCoverImage: boolean) {
    let formData = new FormData();
    formData.append('file_id', fileName);
    formData.append('file_type', 'menu');
    formData.append('is_public', 'true');
    formData.append('file', file);
    this.addMenuImage(formData, isCoverImage);
  }

  addMenuImage(formData: any, isCoverImage: boolean) {
    this.ngxSpinnerService.show();
    this.menuService.addMenuImage(formData).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          this.removeImage();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          this.removeImage();
          return;
        }
        if (isCoverImage) {
          this.productForm.get('image_url')?.setValue(response.data.url);
        } else {
          this.addAdditionalImage(response.data.url);
        }

        this.ngxSpinnerService.hide();
      },
      error: (error) => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
        this.removeImage();
      },
    });
  }

  openCharacteristicsDialog(characteristic?: any): void {
    const dialogRef = this.dialog.open(AddCharacteristicsModalComponent, {
      data: { characteristic: characteristic ? characteristic : null },
      width: '450px',
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result !== undefined && result != '') {
        if (characteristic) {
          let findIndex = this.productForm
            .get('characteristics')
            ?.value.findIndex((f: any) => f.name == characteristic.name);
          const characteristicGroup = this.characteristics.at(findIndex) as FormGroup;
          characteristicGroup.patchValue({ name: result.name, value: result.value });
        } else {
          this.addCharacteristics(-1, result.name, result.value);
        }
      }
    });
  }

  removeCharacteristic(index: any) {
    this.characteristics.removeAt(index);
  }

  openCustomizationDialog(customization?: any): void {
    const dialogRef = this.dialog.open(AddCustomizationModalComponent, {
      data: {
        customization: customization ? customization : null,
        allCustomization: this.customizations,
      },
      // The same full-screen sheet the Customizations page opens, so both
      // entry points look alike. It stays a dialog rather than a route so the
      // menu being written here is not torn down.
      width: '100vw',
      maxWidth: '100vw',
      height: '100vh',
      panelClass: 'czm-fullscreen-dialog',
      // Material's scale-in would fight the sheet's slide-up.
      enterAnimationDuration: '0ms',
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result !== undefined && result != '') {
        if (customization) {
        } else {
          this.saveCustomization(result);
        }
      }
    });
  }

  saveCustomization(customization: any) {
    let options: any = [];
    let isCheckedValue = '';
    let isCheckedValues: any = [];
    customization.options.forEach((f: any) => {
      options.push({
        value: f.name,
        description: f.description ?? '',
        price: f.price ? f.price : 0,
        is_default: customization.inputType == 'Text Field' ? true : f.isChecked,
      });
      if (f.isChecked && customization.inputType != 'Multi Selection') {
        isCheckedValue = f.name;
      } else if (f.isChecked && customization.inputType == 'Multi Selection') {
        isCheckedValues.push(f.name);
      }
    });

    let payload = {
      name: customization.name,
      types: options,
      remarks: customization.remarks,
      default: customization.defaultValue,
      is_multi_select: customization.inputType == 'Multi Selection' ? true : false,
      is_price_different: customization.priceDiffersForEachOption,
      is_customer_visible: customization.isCustomerVisible ?? true,
    };

    this.ngxSpinnerService.show();
    this.customizationService.addCustomization(payload).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        this.ngxSpinnerService.hide();
        this.toastr.success('Customization added successfully', 'Success');
        let newCustomization = response.data;
        this.customizations.push(newCustomization);
        this.dropdownList = [
          ...this.dropdownList,
          { item_id: newCustomization.id, item_text: newCustomization.name },
        ];
        let newOptions: any = [];
        newCustomization.types.forEach((t: any) => {
          newOptions.push({
            id: t.type_id,
            name: t.value,
            isChecked: t.is_default,
            isEditable: !newCustomization.is_price_different,
          });
        });
        // Ensure the new customization is reflected in the form array and selected in the multiselect
        this.addCustomization1(
          newCustomization.id,
          customization.name,
          customization.inputType,
          newOptions,
          customization.optionsWithPrice,
        );
        const ctrl = this.productForm.get('selectedCustomizations');
        if (ctrl) {
          const current = ctrl.value || [];
          const newSelection = { item_id: newCustomization.id, item_text: newCustomization.name };
          if (!current.some((s: any) => s.item_id === newSelection.item_id)) {
            ctrl.setValue([...current, newSelection]);
            ctrl.markAsDirty();
          }
        }
        // this.getCustomizations();
      },
      error: (error: any) => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  removeCustomization(index: any, name: any) {
    const id = this.customization.at(index).get('id')?.value;
    this.customization.removeAt(index);
    // Deselect from multiselect control
    const ctrl = this.productForm.get('selectedCustomizations');
    if (ctrl) {
      const current = ctrl.value || [];
      ctrl.setValue(current.filter((s: any) => s.item_id !== id));
      ctrl.markAsDirty();
    }
  }

  onSingleClassificationChange(event: any, classification: any, option: any, i: any, j: any) {
    const options = this.classification.controls[i].get('options') as FormArray;
    options.controls.forEach((option) => {
      if (option.value.name == event.target.value) {
        option.get('isChecked')?.setValue(true);
      } else {
        option.get('isChecked')?.setValue(false);
      }
    });
    // Trigger validation on the classification form group and mark as touched
    const classificationGroup = this.classification.controls[i] as FormGroup;
    classificationGroup.markAsTouched();
    classificationGroup.updateValueAndValidity();
  }

  onMultiClassificationChange(event: any, classification: any, option: any, i: any, j: any) {
    const options = this.classification.controls[i].get('options') as FormArray;
    options.controls.forEach((option) => {
      if (option.value.name == event.target.value) {
        if (option.value.isChecked) option.get('isChecked')?.setValue(false);
        else option.get('isChecked')?.setValue(true);
      }
    });
    // Trigger validation on the classification form group and mark as touched
    const classificationGroup = this.classification.controls[i] as FormGroup;
    classificationGroup.markAsTouched();
    classificationGroup.updateValueAndValidity();
  }

  onSingleCustomizationChange(event: any, customization: any, option: any, i: any, j: any) {
    const options = this.customization.controls[i].get('options') as FormArray;
    options.controls.forEach((option) => {
      if (option.value.name == event.target.value) {
        option.get('isChecked')?.setValue(true);
      } else {
        option.get('isChecked')?.setValue(false);
      }
    });
  }

  onMultipleCustomizationChange(event: any, customization: any, option: any, i: any, j: any) {
    const options = this.customization.controls[i].get('options') as FormArray;
    options.controls.forEach((option) => {
      if (option.value.name == event.target.value && event.target.checked) {
        option.get('isChecked')?.setValue(true);
      } else if (option.value.name == event.target.value && !event.target.checked) {
        option.get('isChecked')?.setValue(false);
      }
    });
  }

  getSpicy(count: any) {
    let x = [];
    let i = 0;

    while (i < count) {
      x.push(i);
      i++;
    }
    return x;
  }

  onSingleCustomizationChange1(value: any, customization: any, option: any, i: any, j: any) {
    const options = this.customization.controls[i].get('options') as FormArray;
    options.controls.forEach((option) => {
      if (option.value.name == value) {
        option.get('isChecked')?.setValue(true);
      } else {
        option.get('isChecked')?.setValue(false);
      }
    });
  }

  getTypesByRestaurant() {
    this.displayLoading = true;
    this.menuService.getTypesByRestaurant().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.displayLoading = false;
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.displayLoading = false;
          return;
        }
        this.exitingTypes = response.data.types;

        this.exitingTypes.forEach((e: any) => {
          var categories: any = [];
          if (e.categories) {
            e.categories.forEach((c: any) => {
              categories.push({ id: c.id, name: c.name, isChecked: false });
            });
            this.addClassification(e.id, e.name, e.is_multi_category, categories, e.is_mandatory);
          }
        });
        this.displayLoading = false;
      },
      error: (error) => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.displayLoading = false;
      },
    });
  }

  getExitingSKU() {
    this.displayLoading = true;
    this.menuService.getExitingSKU().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.displayLoading = false;
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.displayLoading = false;
          return;
        }
        this.exitingSKUs = response.data;

        this.displayLoading = false;
      },
      error: (error) => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.displayLoading = false;
      },
    });
  }

  getServingTimes() {
    this.ngxSpinnerService.show();
    this.menuService.getServingTime().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        this.servingTimes = response.data;
        this.ngxSpinnerService.hide();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  addNewServingTime() {
    const dialogRef = this.dialog.open(AddServingTimeModalComponent, {
      data: { allServingTimes: this.servingTimes },
      width: '500px',
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result !== undefined && result != '') {
        this.saveServingTime(result);
      }
    });
  }

  saveServingTime(servingTime: any) {
    this.ngxSpinnerService.show();
    this.menuService.addServingTime(servingTime).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        let newServingTime = {
          id: response.data.serving_time.id,
          name: response.data.serving_time.name,
          start_time: response.data.serving_time.startTime,
          end_time: response.data.serving_time.endTime,
        };
        this.servingTimes.push(newServingTime);
        this.ngxSpinnerService.hide();
      },
      error: (error) => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  getStations() {
    this.displayLoading = true;
    this.stationService.getStations().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.displayLoading = false;
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.displayLoading = false;
          return;
        }
        this.stations = response.data?.filter((s: any) => s.station_type == 'kitchen');
        if (!this.exitingMenuId) {
          this.selectedStation = this.stations.find((s: any) => s.is_default);
        } else if (this.exitingMenu && !this.selectedStation) {
          this.selectedStation = this.stations.find(
            (s: any) => s.station_id == this.exitingMenu.station_id,
          );
        }
        this.displayLoading = false;
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.displayLoading = false;
      },
    });
  }

  changeStation(station: any) {
    this.selectedStation = station;
  }

  createNewStation() {
    const dialogRef = this.dialog.open(AddStationModalComponent, {
      data: { allStations: this.stations, isFromMenu: true },
      width: '500px',
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result !== undefined && result != '') {
        this.saveStation(result);
      }
    });
  }

  saveStation(station: any) {
    this.ngxSpinnerService.show();
    this.stationService.addStation(station).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        this.ngxSpinnerService.hide();
        this.stations.push(response.data);
        // this.getStations();
      },
      error: (error) => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  getExitingCustomizations() {
    this.displayLoading = true;
    this.customizationService.getCustomizations().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.displayLoading = false;
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.displayLoading = false;
          return;
        } else {
          this.customizations = response.data;
          this.customizations.forEach((c: any) => {
            if (!c.external_references?.uber)
              this.dropdownList = [...this.dropdownList, { item_id: c.id, item_text: c.name }];
          });
          this.dropdownSettings = {
            singleSelection: false,
            idField: 'item_id',
            textField: 'item_text',
            selectAllText: 'Select All',
            unSelectAllText: 'UnSelect All',
            itemsShowLimit: 2,
            allowSearchFilter: true,
            defaultOpen: false,
          };
        }
        this.displayLoading = false;
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.displayLoading = false;
      },
    });
  }

  addExitingCustomization(
    id: number,
    name: string,
    inputType: string,
    options: any[],
    defaultOption: any,
    isEditable: boolean,
  ) {
    if (inputType == 'Multi Selection' && !Array.isArray(defaultOption)) {
      defaultOption = [defaultOption];
    }

    options.forEach((option) => {
      if (inputType == 'Text Field') {
        option['isChecked'] = true;
      } else if (inputType == 'Single Selection' && option.value == defaultOption) {
        option['isChecked'] = true;
      } else if (
        inputType == 'Multi Selection' &&
        defaultOption &&
        defaultOption.length > 0 &&
        defaultOption.find((f: any) => f == option.value)
      ) {
        option['isChecked'] = true;
      } else {
        option['isChecked'] = false;
      }
    });

    this.customization.push(
      this.fb.group({
        id: [id, Validators.required],
        name: [name, Validators.required],
        inputType: [inputType],
        isEditable: isEditable,
        options: this.fb.array(
          options.map((option) =>
            this.fb.group({
              name: [option.value, Validators.required],
              isChecked: [option.is_default],
              price: [option.price],
              type_id: [option.type_id],
              isEditable: isEditable,
            }),
          ),
        ),
      }),
    );
  }

  onItemSelect(event: any): void {
    let findCustomization = this.customizations.find((c: any) => c.id == event.item_id);
    let findInputType = findCustomization.is_multi_select
      ? 'Multi Selection'
      : findCustomization.types.length == 1
        ? 'Text Field'
        : 'Single Selection';

    // Check for duplicate customization
    const existingCustomization = this.customization.value.find(
      (c: any) => c.id === findCustomization.id,
    );
    if (existingCustomization) {
      return; // Skip adding duplicate
    }

    this.addExitingCustomization(
      findCustomization.id,
      findCustomization.name,
      findInputType,
      findCustomization.types,
      findCustomization.default,
      !findCustomization.is_price_different,
    );
  }

  onSelectAll(items: any): void {
    if (Array.isArray(items)) {
      items.forEach((it: any) => this.onItemSelect(it));
    }
  }

  onItemDeSelect(item: any): void {
    const idx = this.customization.value.findIndex((f: any) => f.id === item.item_id);
    if (idx > -1) {
      this.customization.removeAt(idx);
    }
  }

  onDeSelectAll(_: any): void {
    // remove any customization that came from dropdown selections
    const ids = new Set(this.dropdownList.map((d: any) => d.item_id));
    for (let i = this.customization.length - 1; i >= 0; i--) {
      const id = this.customization.at(i).get('id')?.value;
      if (ids.has(id)) this.customization.removeAt(i);
    }
  }

  private syncSelectedCustomizationsFromFormArray(): void {
    const preselected = (this.customization?.value || [])
      .map((c: any) => ({ item_id: c.id, item_text: c.name }))
      .filter((it: any) => this.dropdownList.some((d: any) => d.item_id === it.item_id));
    const ctrl = this.productForm.get('selectedCustomizations');
    if (ctrl) {
      ctrl.setValue(preselected);
      ctrl.markAsPristine();
    }
  }

  onImageError(event: Event): void {
    (event.target as HTMLImageElement).src = 'assets/empty-menu.png';
  }

  onMenuThumbnailImageError(event: Event, itemId: string | number): void {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = 'assets/default/menu-sm.png';
    this.imageErrors.add(itemId);
  }

  hasImageError(itemId: string | number): boolean {
    return this.imageErrors.has(itemId);
  }

  generateMenuCoverImage() {
    if (!this.exitingMenuId || this.exitingMenuId === '' || this.exitingMenuId === null) {
      this.toastr.error('Menu ID is required to generate cover image.', 'Error');
      return;
    }
    if (this.generating.image) return;

    const imageUrl = this.productForm.get('image_url');
    const imageAtStart = imageUrl?.value;
    this.runGeneration(
      'image',
      this.menuService.generateMenuCoverImage(this.exitingMenuId),
      (data) => {
        if (!data.s3_url) {
          this.toastr.error('Failed to generate cover image.', 'Error');
          return;
        }
        // The user may have uploaded or removed a cover while this ran; keep theirs.
        if (imageUrl?.value !== imageAtStart) {
          this.toastr.info('Kept the cover image you set.', 'Generated image not applied');
          return;
        }
        imageUrl?.setValue(data.s3_url);
        this.toastr.success('Cover image generated successfully', 'Success');
      },
    );
  }

  addCategory(classification: any) {
    // Its own page now, like Add New Type; park the menu and come back to it.
    // The page saves the category itself, so the refreshed type list on return
    // is what brings it into the classification options.
    this.saveDraft();
    this.router.navigate(['/add-category', classification.value.id], {
      queryParams: { returnTo: this.router.url },
    });
  }

  // ------------------------------------------------------ in-progress draft

  /**
   * Adding a type or a customization happens on its own page, so the menu
   * being written here is parked first and picked up again on return.
   * Session storage, not local: a draft should not outlive the tab.
   */
  private static readonly DRAFT_KEY = 'fnf-menu-draft';

  private saveDraft() {
    const draft = {
      route: this.router.url,
      value: this.productForm.getRawValue(),
      menuIngredients: this.menuIngredients,
      tagsList: this.tagsList,
      imagePreview: this.imagePreview,
      // Files cannot be serialised; already-uploaded URLs survive.
      images: this.images.map((image) => ({ file: null, url: image.url })),
      selectedItems: this.selectedItems,
      selectedStation: this.selectedStation,
      selectedStatus: this.selectedStatus,
      servingTime: this.servingTimeForm?.getRawValue() ?? null,
    };
    try {
      sessionStorage.setItem(AddMenuComponent.DRAFT_KEY, JSON.stringify(draft));
    } catch {
      // A full or unavailable store is not worth blocking the navigation for.
    }
  }

  private readDraft(): any | null {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(AddMenuComponent.DRAFT_KEY);
      sessionStorage.removeItem(AddMenuComponent.DRAFT_KEY);
    } catch {
      return null;
    }
    if (!raw) {
      return null;
    }
    try {
      const draft = JSON.parse(raw);
      // Only restore onto the page the draft was written from.
      return draft?.route === this.router.url ? draft : null;
    } catch {
      return null;
    }
  }

  private applyDraft(draft: any) {
    const value = draft.value || {};

    // The arrays are rebuilt from their own sources, so patch the rest only.
    const { classification, customization, characteristics, ingredients, tags, ...scalars } = value;
    this.productForm.patchValue(scalars);

    this.menuIngredients = draft.menuIngredients || [];
    this.tagsList = draft.tagsList || [];
    this.imagePreview = draft.imagePreview ?? null;
    this.images = draft.images || [];
    this.selectedItems = draft.selectedItems || [];
    this.selectedStation = draft.selectedStation ?? this.selectedStation;
    this.selectedStatus = draft.selectedStatus ?? this.selectedStatus;
    if (draft.servingTime && this.servingTimeForm) {
      this.servingTimeForm.patchValue(draft.servingTime);
    }

    this.syncIngredientsAndTags();

    this.additionalImages.clear();
    for (const image of this.images) {
      if (image.url) {
        this.additionalImages.push(this.fb.control(image.url));
      }
    }

    // Ticked categories are reapplied by name once the type list rebuilds the
    // classification array, which is what setExitingCustomization() already does.
    this.existingCustomization = (classification || []).map((type: any) => ({
      name: type.name,
      categories: (type.options || [])
        .filter((option: any) => option.isChecked)
        .map((option: any) => ({ name: option.name })),
    }));
    if (this.isCustomizationLoaded) {
      this.setExitingCustomization();
    }
  }

  /** Mirror the plain arrays back into their form arrays. */
  private syncIngredientsAndTags() {
    this.ingredients.clear();
    for (const ingredient of this.menuIngredients) {
      this.ingredients.push(this.fb.control(ingredient));
    }
    this.tags.clear();
    for (const tag of this.tagsList) {
      this.tags.push(this.fb.control(tag));
    }
  }

  addNewClassification() {
    // Adding a type is its own page now; park the menu and come back to it.
    this.saveDraft();
    this.router.navigate(['/add-type'], { queryParams: { returnTo: this.router.url } });
  }

  saveNewType(type: any) {
    this.ngxSpinnerService.show();
    this.restaurantService.addRestaurantType(type).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }

        // Extract the new type from the response
        // The API might return the type in different formats:
        // 1. response.data.type - type object
        // 2. response.data - directly the type object
        let newType: any = null;
        let needsRefetch = false;

        if (response.data.type) {
          newType = response.data.type;
        } else if (response.data.id && response.data.name) {
          // response.data is the type object itself
          newType = response.data;
        } else {
          // If API doesn't return the type with ID, we need to refetch to get the real ID
          needsRefetch = true;
          // Store the type name for later matching
          newType = {
            id: null, // Will be set after refetch
            name: type.name,
            is_multi_category: type.is_multi_category,
            is_mandatory: type.is_mandatory,
            categories: type.categories || [],
          };
        }

        if (needsRefetch || !newType.id || newType.id.toString().startsWith('temp_')) {
          // Refetch types to get the real ID
          this.menuService.getTypesByRestaurant().subscribe({
            next: (typesResponse: FNFResponse<any>) => {
              if (typesResponse.data && typesResponse.data.types) {
                // Find the newly added type by name and attributes
                const addedType = typesResponse.data.types.find(
                  (t: any) =>
                    t.name === type.name &&
                    t.is_multi_category === type.is_multi_category &&
                    t.is_mandatory === type.is_mandatory,
                );

                if (addedType) {
                  newType = addedType;
                  this.addTypeToForm(newType);
                  this.toastr.success(
                    response.data.message || 'Type added successfully',
                    'Success',
                  );
                } else {
                  this.toastr.error(
                    'Type was added but could not be found. Please refresh the page.',
                    'Error',
                  );
                }
              } else {
                this.toastr.error(
                  'Type was added but could not be retrieved. Please refresh the page.',
                  'Error',
                );
              }
              this.ngxSpinnerService.hide();
            },
            error: () => {
              this.toastr.error(
                'Type was added but could not be retrieved. Please refresh the page.',
                'Error',
              );
              this.ngxSpinnerService.hide();
            },
          });
        } else {
          // We have the real type with ID, add it directly
          this.addTypeToForm(newType);
          this.toastr.success(response.data.message || 'Type added successfully', 'Success');
          this.ngxSpinnerService.hide();
        }
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  private addTypeToForm(newType: any) {
    // Check if type already exists in the form (might have been added with temp ID)
    const existingClassificationIndex = this.classification.controls.findIndex((ctrl: any) => {
      const ctrlName = ctrl.get('name')?.value?.toLowerCase();
      const ctrlId = ctrl.get('id')?.value;
      // Match by name, or by ID if it's a temp ID
      return (
        ctrlName === newType.name?.toLowerCase() ||
        (ctrlId &&
          ctrlId.toString().startsWith('temp_') &&
          ctrlName === newType.name?.toLowerCase())
      );
    });

    // Prepare categories array for the form
    const categories: any[] = [];
    if (newType.categories && Array.isArray(newType.categories) && newType.categories.length > 0) {
      newType.categories.forEach((c: any) => {
        // Handle both string categories and object categories
        if (typeof c === 'string') {
          categories.push({
            id: null, // Will be set when API returns the actual ID
            name: c,
            isChecked: false,
          });
        } else if (c && typeof c === 'object') {
          categories.push({
            id: c.id || null,
            name: c.name || c.value || c,
            isChecked: false,
          });
        }
      });
    }

    if (existingClassificationIndex !== -1) {
      // Update existing classification with real ID
      const existingClassification = this.classification.at(
        existingClassificationIndex,
      ) as FormGroup;
      existingClassification.patchValue({
        id: newType.id,
        name: newType.name,
        isMultiSelect: newType.is_multi_category || false,
        isMandatory: newType.is_mandatory || false,
      });

      // Update categories in the options FormArray
      const optionsArray = existingClassification.get('options') as FormArray;
      optionsArray.clear();
      categories.forEach((cat: any) => {
        optionsArray.push(
          this.fb.group({
            id: [cat.id, Validators.required],
            name: [cat.name, Validators.required],
            isChecked: [cat.isChecked],
          }),
        );
      });

      // Trigger validation
      existingClassification.updateValueAndValidity();
    } else {
      // Add the new type to the classification FormArray
      this.addClassification(
        newType.id,
        newType.name,
        newType.is_multi_category || false,
        categories,
        newType.is_mandatory || false,
      );
    }

    // Update exitingTypes array to keep it in sync
    // Format categories for exitingTypes (ensure they have id and name)
    const formattedCategories = (newType.categories || []).map((c: any) => {
      if (typeof c === 'string') {
        return { id: null, name: c };
      } else if (c && typeof c === 'object') {
        return { id: c.id || null, name: c.name || c.value || c };
      }
      return c;
    });

    const typeData = {
      id: newType.id,
      name: newType.name,
      is_multi_category: newType.is_multi_category || false,
      is_mandatory: newType.is_mandatory || false,
      categories: formattedCategories,
    };

    // Check if type already exists to avoid duplicates
    const existingTypeIndex = this.exitingTypes.findIndex(
      (t: any) => t.id === newType.id || t.name?.toLowerCase() === newType.name?.toLowerCase(),
    );

    if (existingTypeIndex === -1) {
      this.exitingTypes.push(typeData);
    } else {
      // Update existing type with real ID
      this.exitingTypes[existingTypeIndex] = typeData;
    }

    // Trigger change detection to update the UI
    this.cdr.detectChanges();
  }
}
